import { useMemo } from "react";
import { Button, Stack } from "@mui/material";
import { Switches } from "mui-rff";
import { useField, useForm } from "react-final-form";
import { createListCollection } from "@ark-ui/react";

import { LayerTypes } from "@/types/map";
import OrderListButton from "@/features/admin/components/order-list-button/OrderListButton";
import { TextField } from "@/features/shared/components";
import Select from "@/components/select/select";
import { required } from "@/features/shared/components/mui-rff-fields/validation-helpers";
import { useTranslation } from "@/lib/i18n";
import {
  EdrObservationsMapLayer,
  MapWidgetConfigurationAllOfLayers,
  PrecipitationMapLayer,
  StaticOverlayMapLayer,
  WmsMapLayer,
} from "@/lib/api";

import EDRConfiguration from "./edr/EDRConfiguration";
import PercipitationConfiguration from "./precipitation/PrecipitationConfiguration";
import StaticConfiguration from "./static/StaticConfiguration";
import WMSConfiguration from "./wms/WMSConfiguration";
import ZoomConfiguration, { ZoomLevelOptions } from "./ZoomConfiguration";

interface MapLayerConfigurationProps {
  name: string;
  label?: string;
  initialValue: MapWidgetConfigurationAllOfLayers;
  removeLayer: () => void;
  index: number;
  layers: unknown[];
  allowedLayerTypes?: LayerTypes[];
}

const MapLayerConfiguration = ({
  index,
  name,
  label,
  initialValue,
  removeLayer,
  layers,
  allowedLayerTypes,
}: MapLayerConfigurationProps) => {
  const { t } = useTranslation();
  const layerTypes = useMemo(() => {
    return Object.entries(LayerTypes)
      .filter(
        ([, layerType]) => !allowedLayerTypes || allowedLayerTypes.includes(layerType),
      )
      .map((option) => ({
        label: t(`admin.widgets.fields.map.${option[1]}.label`),
        value: option[1],
      }));
  }, [LayerTypes, allowedLayerTypes]);
  const layerTypeCollection = createListCollection({
    items: layerTypes,
  });

  const form = useForm();
  const typeField = useField(`${name}[${index}].type`, {
    defaultValue: layerTypes.length === 1 ? layerTypes[0].value : undefined,
    validate: required,
  });
  const enabledField = useField(`${name}[${index}].enabled_by_default`, {
    type: "checkbox",
    defaultValue: initialValue.enabled_by_default ?? false,
  });

  return (
    <Stack border={"1px solid rgba(0,0,0, 0.1)"} padding={2} gap={1}>
      {layers.length > 1 && <OrderListButton list={layers} name={name} index={index} />}

      <TextField
        sx={{ mt: 0 }}
        name={`${name}[${index}].title`}
        label={t("admin.widgets.fields.map.layer.label")}
        defaultValue={initialValue.title}
        required
      />

      <Select
        label={label}
        collection={layerTypeCollection}
        placeholder="Select a layer type"
        error={typeField.meta.touched && typeField.meta.error}
        value={typeField.input.value}
        onValueChange={(value) => form.change(typeField.input.name, value)}
      />

      {typeField.input.value === LayerTypes.WMS && (
        <>
          <WMSConfiguration
            name={`${name}[${index}]`}
            initialValue={initialValue as WmsMapLayer}
          />
          <ZoomConfiguration
            name={`${name}[${index}]`}
            initialValue={initZoomOptions(initialValue)}
          />
        </>
      )}
      {typeField.input.value === LayerTypes.EDR_OBSERVATIONS && (
        <EDRConfiguration
          name={`${name}[${index}]`}
          initialValue={initialValue as EdrObservationsMapLayer}
        />
      )}
      {typeField.input.value === LayerTypes.STATIC && (
        <StaticConfiguration
          name={`${name}[${index}]`}
          initialValue={initialValue as StaticOverlayMapLayer}
        />
      )}
      {typeField.input.value === LayerTypes.PRECIPITATION && (
        <>
          <PercipitationConfiguration
            name={`${name}[${index}]`}
            initialValue={initialValue as PrecipitationMapLayer}
          />
          <ZoomConfiguration
            name={`${name}[${index}]`}
            initialValue={initZoomOptions(initialValue)}
          />
        </>
      )}

      <Switches
        {...enabledField.input}
        data={{
          label: t("admin.widgets.fields.map.layer.enabledByDefault"),
          value: false,
        }}
        required
      />

      <Button onClick={removeLayer}>{t("admin.widgets.fields.map.layer.remove")}</Button>
    </Stack>
  );
};

export default MapLayerConfiguration;

function initZoomOptions(
  initialValues: MapWidgetConfigurationAllOfLayers,
): ZoomLevelOptions {
  const cast = initialValues as ZoomLevelOptions;
  return {
    min_allowed_zoom_level_override: cast.min_allowed_zoom_level_override,
    max_allowed_zoom_level_override: cast.max_allowed_zoom_level_override,
  };
}
